<!-- src/components/base/SohaDropdown.vue -->
<template>
  <div class="soha-dropdown">
    <!-- حالت custom content (وقتی slot داریم) -->
    <div
      v-if="$slots.default"
      class="soha-dropdown-custom" 
    >
      <Button 
        :outlined="outline"
        :class="buttonClasses"
        :label="text"
        :icon="icon"
        :size="primeSize"
        :severity="buttonSeverity"
        @click="toggleCustomDropdown"
      >
        <slot name="button-content">
          {{ text }}
        </slot>
      </Button>
      
      <OverlayPanel 
        ref="op"
        :class="menuClass"
        @hide="$emit('hide')"
      >
        <slot />
      </OverlayPanel>
    </div>

    <!-- حالت معمولی با options -->
    <Dropdown 
      v-else
      v-model="selectedItem"
      :options="formattedItems"
      :placeholder="text"
      :class="dropdownClasses"
      option-label="label"
      @change="onChange"
      @hide="$emit('hide')"
    >
      <template #value="slotProps">
        <slot
          name="button-content"
          :value="slotProps.value"
        >
          <div class="dropdown-toggle-content">
            <div v-if="slotProps.value">
              <span>{{ slotProps.value.label }}</span>
            </div>
          </div>
        </slot>
      </template>
      
      <template #option="slotProps">
        <slot
          name="dropdown-item"
          :option="slotProps.option"
        >
          <div
            class="dropdown-item-content"
            @click="slotProps.option.command ? slotProps.option.command() : null"
          >
            <i
              v-if="slotProps.option.icon"
              :class="slotProps.option.icon"
              class="dropdown-item-icon"
            />
            <span class="dropdown-item-text">{{ slotProps.option.label }}</span>
            <span
              v-if="slotProps.option.badge"
              class="dropdown-item-badge"
            >
              {{ slotProps.option.badge }}
            </span>
          </div>
        </slot>
      </template>
    </Dropdown>
  </div>
</template>

<script>
import Dropdown from 'primevue/dropdown';
import OverlayPanel from 'primevue/overlaypanel';
import Button from 'primevue/button';

export default {
  name: 'SohaDropdown',

  components: {
    Dropdown,
    OverlayPanel,
    Button
  },

  props: {
    text: {
      type: String,
      default: null,
    },
    variant: {
      type: String,
      default: 'link'
    },
    size: {
      type: String,
      default: 'sm'
    },
    modelValue: {
      type: String,
      default: null,
    },
    right: Boolean,
    noCaret: Boolean,
    noFlip: Boolean,
    toggleClass: {
      type: String,
      default: null,
    },
    menuClass: {
      type: String,
      default: null,
    },
    icon: {
      type: String,
      default: null,
    },
    outline: {
      type: Boolean,
      default: false
    },
    items: {
      type: Array,
      default: () => []
    }
  },

  emits: ['update:modelValue', 'input', 'change', 'hide'],

  data() {
    return {
      selectedItem: null
    };
  },

  computed: {
    primeSize() {
      const sizeMap = {
        'sm': 'small',
        'md': null,
        'lg': 'large'
      };
      return sizeMap[this.size] || null;
    },

    buttonSeverity() {
      const variantMap = {
        'primary': 'primary',
        'secondary': 'secondary', 
        'success': 'success',
        'danger': 'danger',
        'warning': 'warning',
        'info': 'info',
        'help': 'help',
        'link': null
      };
      return variantMap[this.variant] || 'primary';
    },

    buttonClasses() {
      return [
        this.toggleClass,
        'soha-dropdown-button',
        {
          'soha-dropdown-right': this.right,
          'soha-dropdown-no-caret': this.noCaret
        }
      ];
    },

    formattedItems() {
      return this.items.map(item => ({
        label: item.text || item.title || item.label,
        value: item,
        icon: item.icon,
        badge: item.badge,
        command: item.command
      }));
    },

    selectedValue: {
      get() {
        return this.selectedItem;
      },
      set(value) {
        this.selectedItem = value;
        this.$emit('update:modelValue', value);
      }
    }
  },

  methods: {
    onChange(event) {
      this.$emit("change", event.value);
      this.$emit("input", event.value);
    },
    toggleCustomDropdown(event) {
      this.$refs.op.toggle(event);
    }
  },

  watch: {
   modelValue: {
      immediate: true,
      handler(newVal) {
        this.selectedItem = newVal?.id;
      }
    }
  },
};
</script>

<style scoped>
.soha-dropdown {
  display: inline-block;
}
.soha-dropdown-button {
  color: inherit;
}
/* برای حالت link که outline داره */
.soha-dropdown-button.p-button-text {
  background: transparent;
  border: none;
}
.soha-dropdown-button.p-button {
  background: transparent;
  border: none;
  font-size: inherit;
}

.soha-dropdown-button.p-button:hover {
  background: transparent;
  border: none;
  color: inherit;
}

:deep(.p-dropdown) {
  background: transparent !important;
  border: none !important;
}

:deep(.p-dropdown .p-dropdown-label) {
  padding: 0.5rem;
}

.dropdown-item-content {
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
}

.dropdown-item-icon {
  margin-right: 0.5rem;
}

.dropdown-item-badge {
  margin-left: auto;
  background: #e9ecef;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
}
</style>